import { useEffect, useState, MouseEvent } from 'react';
import { Header, MouseEventsContainer } from './components';
import { setSyles, resizeAndMovementFunctions } from './helpers';
import { useOutsideClick } from './hooks';
import * as Styled from './styled';
import { defaults, DragNDropAndResizableType, resizeData } from './types';

export { type OnChangeProps, type DragNDropAndResizableType } from './types';

const FOCUS_ADD_Z_INDEX = 2000;

export const DragNDropAndResizable = (data: DragNDropAndResizableType) => {
  if (!data.isOpened) return null;

  return <DragNDropAndResizableBody {...data} />;
};

export const DragNDropAndResizableBody = (data: DragNDropAndResizableType) => {
  const [t, setT] = useState(data.top ?? defaults.top);
  const [l, setL] = useState(data.left ?? defaults.left);
  const [w, setW] = useState(data.width ?? defaults.width);
  const [h, setH] = useState(data.height ?? defaults.height);

  const [startT, setStartT] = useState<number | undefined>();
  const [startL, setStartL] = useState<number | undefined>();

  const [resizeH, setResizeH] = useState<resizeData | undefined>();
  const [resizeW, setResizeW] = useState<resizeData | undefined>();

  const [activeMouse, setActiveMouse] = useState(false);
  const [isDndOrResize, setIsDndOrResize] = useState(false);

  const [additionalZ, setAdditionalZ] = useState(FOCUS_ADD_Z_INDEX);

  const minWidth = data.minWidth ?? defaults.minWidth;
  const minHeight = data.minHeight ?? defaults.minHeight;

  const resizePaddings = data.resizePaddings ?? defaults.resizePaddings;

  const resetResize = () => {
    if (resizeH || resizeW)
      data.onChange?.call(null, {
        top: t,
        left: l,
        width: w,
        height: h,
        changed: 'resize',
      });

    if (resizeH) setResizeH(undefined);
    if (resizeW) setResizeW(undefined);
  };

  const resetMovement = () => {
    if ((startT || startL) && !resizeH && !resizeW)
      data.onChange?.call(null, {
        top: t,
        left: l,
        width: w,
        height: h,
        changed: 'movement',
      });

    if (startT) setStartT(undefined);
    if (startL) setStartL(undefined);
  };

  const positionParams = {
    t,
    setT,
    l,
    setL,
    w,
    setW,
    h,
    setH,
    startT,
    setStartT,
    startL,
    setStartL,
    resizeH,
    setResizeH,
    resizeW,
    setResizeW,
    minWidth,
    minHeight,
    resetResize,
    resetMovement,
  };

  const { resolveResizeW, resolveResizeH, resolveMovement } =
    resizeAndMovementFunctions(positionParams);

  useOutsideClick(data.id, () => setAdditionalZ(0));

  const resolveResize = (event: MouseEvent<HTMLElement>) => {
    resolveResizeW(event);
    resolveResizeH(event);
  };

  const mouseMove = (event: MouseEvent<HTMLElement>) => {
    resolveMovement(event);
    resolveResize(event);
  };

  const stopMouseMove = () => {
    resetMovement();
    resetResize();
  };

  const resolveDndOrResizeStoped = () => {
    if (isDndOrResize) return;

    setActiveMouse(false);
  };

  const resolvetIsDndOrResize = () => {
    if (!!startT || !!startL || !!resizeH || !!resizeW) {
      setIsDndOrResize(true);
      return;
    }

    setIsDndOrResize(false);
  };

  useEffect(() => {
    resolvetIsDndOrResize();
  }, [startT, startL, resizeH, resizeW]);

  useEffect(() => {
    resolveDndOrResizeStoped();
  }, [isDndOrResize]);

  const pointerEvents = () => {
    if (isDndOrResize) return 'all';
    return activeMouse ? 'all' : 'none';
  };
  const zIndexWrrapper = data.zIndexWrrapper ?? defaults.zIndexWrrapper;

  return (
    <Styled.WindowWrapper
      id={data.id}
      style={{
        zIndex: zIndexWrrapper + additionalZ,
        pointerEvents: pointerEvents(),
        userSelect: 'none',
      }}
      onMouseUp={stopMouseMove}
      onMouseLeave={stopMouseMove}
      onMouseMove={mouseMove}
    >
      <MouseEventsContainer
        id={data.id}
        style={setSyles({
          t: t - resizePaddings / 2,
          l: l - resizePaddings / 2,
          w: w + resizePaddings,
          h: h + resizePaddings,
          z: data.zIndex,
          sx: data.sx,
        })}
        onMouseEnterDo={() => setActiveMouse(true)}
        onMouseLeaveDo={resolveDndOrResizeStoped}
        setResizeH={setResizeH}
        setResizeW={setResizeW}
        onMouseDownDo={() => setAdditionalZ(FOCUS_ADD_Z_INDEX)}
        w={w}
        h={h}
        resizePaddings={resizePaddings}
      >
        <data.container>
          <Header
            startT={startT}
            setStartT={setStartT}
            setStartL={setStartL}
            headerLeft={data.headerLeft}
            onClose={data.onClose}
            t={t}
            l={l}
            w={w}
            h={h}
            setT={setT}
            setL={setL}
            setW={setW}
            setH={setH}
            windowOnChange={data.onChange}
          />

          <Styled.Body>{data.body}</Styled.Body>
        </data.container>
      </MouseEventsContainer>
    </Styled.WindowWrapper>
  );
};
